
import React, { useState } from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { MagneticEffect } from './MagneticEffect';
import { PlayCircleIcon } from './icons/PlayCircleIcon';
import { useTranslation } from '../i18n/LanguageContext';

interface VideoTestimonialItem {
  videoId: string;
  studentName: string;
  studentDescription: string;
  title: string;
  photoUrl: string;
}

export const VideoTestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const videoTestimonialsData: VideoTestimonialItem[] = t('videoTestimonials.items');

  const [activeTestimonial, setActiveTestimonial] = useState<VideoTestimonialItem>(videoTestimonialsData[0]);
  const [showVideo, setShowVideo] = useState(false);

  const handleThumbnailClick = (testimonial: VideoTestimonialItem) => {
    setActiveTestimonial(testimonial);
    setShowVideo(false); 
  };

  const handlePlayClick = () => {
    setShowVideo(true);
  };
  
  return (
    <section id="depoimentos" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('videoTestimonials.title')}
            subtitle={t('videoTestimonials.subtitle')}
          />
        </ScrollAnimation>
        
        <ScrollAnimation animation="slideInUp" delay={150}>
           <div className="max-w-4xl mx-auto">
              {/* Main Video Player */}
              <div className="mb-8">
                  <MagneticEffect strength={10}>
                    <div className="aspect-video shadow-2xl rounded-lg overflow-hidden w-full bg-black relative">
                      {!showVideo ? (
                        <button
                          onClick={handlePlayClick}
                          className="w-full h-full group focus:outline-none"
                          aria-label={`${t('videoTestimonials.playAriaLabel')} ${activeTestimonial.title}`}
                        >
                          <img 
                              src={`https://img.youtube.com/vi/${activeTestimonial.videoId}/maxresdefault.jpg`}
                              onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${activeTestimonial.videoId}/hqdefault.jpg` }}
                              alt={`${t('videoTestimonials.thumbnailAlt')} ${activeTestimonial.title}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                              <PlayCircleIcon className="w-16 h-16 sm:w-20 sm:h-20 group-hover:scale-110 transform transition-transform duration-300" />
                          </div>
                        </button>
                      ) : (
                        <iframe
                          key={activeTestimonial.videoId}
                          className="w-full h-full"
                          src={`https://www.youtube.com/embed/${activeTestimonial.videoId}?autoplay=1&modestbranding=1&rel=0&origin=${window.location.origin}`}
                          title={activeTestimonial.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        ></iframe>
                      )}
                    </div>
                  </MagneticEffect>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-serif font-semibold text-brand-light transition-opacity duration-300">{activeTestimonial.studentName}</h3>
                    <p className="text-brand-text transition-opacity duration-300">{activeTestimonial.studentDescription}</p>
                  </div>
              </div>

              {/* Thumbnails Gallery */}
              <div className="relative pb-4">
                <div className="flex overflow-x-auto space-x-4 sm:space-x-6 py-4 px-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-brand-primary scrollbar-track-transparent">
                  {videoTestimonialsData.map((testimonial) => (
                    <button
                      key={testimonial.videoId}
                      onClick={() => handleThumbnailClick(testimonial)}
                      className={`
                        group relative flex-shrink-0 w-32 sm:w-40 snap-center rounded-lg overflow-hidden 
                        border-2 transition-all duration-300 ease-in-out transform 
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-secondary focus:ring-brand-accent
                        aspect-[3/4]
                        ${activeTestimonial.videoId === testimonial.videoId 
                          ? 'border-brand-accent scale-105 shadow-2xl shadow-brand-accent/20'
                          : 'border-transparent hover:border-slate-500'
                        }
                      `}
                      aria-label={`${t('videoTestimonials.viewAriaLabel')} ${testimonial.studentName}`}
                    >
                      <img 
                        src={testimonial.photoUrl} 
                        alt={`${t('videoTestimonials.photoAlt')} ${testimonial.studentName}`} 
                        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110" 
                        loading="lazy" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                      
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <PlayCircleIcon className="w-12 h-12" />
                      </div>
                      
                      <div className="absolute bottom-0 left-0 right-0 p-2 text-center pointer-events-none">
                        <p className="text-xs font-semibold text-white truncate">{testimonial.studentName}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
        </ScrollAnimation>
      </div>
    </section>
  );
};
